import type { AvailableLocales } from '@eliadmin/core/src/utils/date-holidays-utils';
import { isPublicHoliday } from '@eliadmin/core/src/utils/date-holidays-utils';

interface DateCheckParams {
  date: Date;
  locale: AvailableLocales;
}

interface AddBusinessDaysParams {
  startDate: Date;
  days: number;
  locale: AvailableLocales;
}

interface DeliveryDateRangeParams {
  fromDays: number;
  toDays: number;
  locale: AvailableLocales;
}

export function isHoliday({ date, locale }: DateCheckParams): boolean {
  return isPublicHoliday(date, locale);
}

export function isWeekend({ date }: { date: Date }): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

export function isBusinessDay({ date, locale }: DateCheckParams): boolean {
  return !isWeekend({ date }) && !isHoliday({ date, locale });
}

export function addBusinessDays({ startDate, days, locale }: AddBusinessDaysParams): Date {
  const result = new Date(startDate);
  let addedDays = 0;
  while (addedDays < days) {
    result.setDate(result.getDate() + 1);
    if (isBusinessDay({ date: result, locale })) addedDays++;
  }
  return result;
}

export function getDeliveryDateRange({ fromDays, toDays, locale }: DeliveryDateRangeParams): {
  from: Date;
  to: Date;
} {
  const today = new Date();
  return {
    from: addBusinessDays({ startDate: today, days: fromDays, locale }),
    to: addBusinessDays({ startDate: today, days: toDays, locale }),
  };
}

// Product cards repeat the same handful of lead times. Cache formatted strings,
// not mutable Dates, and expire at a local calendar-day change (including SSR).
let cachedDay = '';
const formattedRanges = new Map<string, string>();
const MAX_CACHED_RANGES = 128;

export function formatDeliveryDateRange({ fromDays, toDays, locale }: DeliveryDateRangeParams): string {
  const today = new Date();
  const day = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  if (cachedDay !== day) {
    formattedRanges.clear();
    cachedDay = day;
  }
  const key = `${locale}:${fromDays}:${toDays}`;
  const existing = formattedRanges.get(key);
  if (existing !== undefined) return existing;

  const from = addBusinessDays({ startDate: today, days: fromDays, locale });
  const to = addBusinessDays({ startDate: today, days: toDays, locale });
  const formatOptions: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'numeric' };
  const displayLocale = locale === 'sk' ? 'sk-SK' : locale;
  const formatted = `${from.toLocaleDateString(displayLocale, formatOptions)} - ${to.toLocaleDateString(displayLocale, formatOptions)}`;
  if (formattedRanges.size >= MAX_CACHED_RANGES) formattedRanges.clear();
  formattedRanges.set(key, formatted);
  return formatted;
}
